// src/components/QueueList.js
import React, { useEffect, useState } from "react";
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getQueue, removeFromQueue } from "../api";
import { decode } from 'html-entities';

const decodeHtmlEntities = (text) => (text ? decode(text) : text);

export default function QueueList({ queue: propQueue, room = "default", onQueueChange }) {
  const [queue, setQueue] = useState(propQueue?.songs ?? []);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [songToRemove, setSongToRemove] = useState(null);

  // Load queue
  useEffect(() => {
    if (Array.isArray(propQueue?.songs)) {
      setQueue(propQueue.songs);
      return;
    }

    const fetchQueue = async () => {
      setLoading(true);
      try {
        const data = await getQueue(room);
        setQueue(data.songs ?? []);
        onQueueChange?.(data.songs ?? []);
      } catch (err) {
        console.error("Failed to fetch queue:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQueue();
  }, [propQueue, room, onQueueChange]);

  // Real-time updates
  useEffect(() => {
    const handleUpdate = (payload) => {
      if (payload.room === room) {
        setQueue(payload.songs);
        onQueueChange?.(payload.songs);
      }
    };

    window.socket?.on("queue:update", handleUpdate);
    return () => window.socket?.off("queue:update", handleUpdate);
  }, [room, onQueueChange]);

  // Handle drag and drop + persist to backend
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(queue);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const newQueue = [...items];
    setQueue(newQueue);
    onQueueChange?.(newQueue);

    // Send new order to backend
    try {
      const songIds = newQueue.map(song => song._id);
      await fetch(`/api/queue/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ room, songIds })
      });
    } catch (err) {
      console.error("Failed to save new queue order:", err);
      // Optionally revert UI on failure
    }
  };

  const handleRemove = (index) => {
    setSongToRemove(index);
    setOpenDialog(true);
  };

  const confirmRemove = async () => {
    if (songToRemove === null) return;
    try {
      await removeFromQueue(songToRemove, room);
    } catch (err) {
      console.error("Failed to remove song:", err);
    } finally {
      setOpenDialog(false);
      setSongToRemove(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <CircularProgress size={24} />
        <span>Loading queue…</span>
      </div>
    );
  }

  if (!Array.isArray(queue) || queue.length === 0) {
    return <Typography>Queue is empty – add some songs!</Typography>;
  }

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="queue">
          {(provided) => (
            <List {...provided.droppableProps} ref={provided.innerRef}>
              {queue.map((song, index) => (
                <Draggable key={song._id || index} draggableId={song._id || index.toString()} index={index}>
                  {(provided, snapshot) => (
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      secondaryAction={
                        <>
                          <Tooltip title="Drag to reorder">
                            <IconButton 
                              {...provided.dragHandleProps} 
                              edge="end" 
                              sx={{ mr: 1 }}
                            >
                              <DragHandleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton edge="end" onClick={() => handleRemove(index)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      }
                      sx={{
                        bgcolor: snapshot.isDragging ? "action.hover" : "transparent",
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={`https://img.youtube.com/vi/${song.videoId}/default.jpg`}
                          variant="square"
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={decodeHtmlEntities(song.title)}
                        secondary={`Added by ${song.addedBy || "anon"}`}
                      />
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      {/* Remove confirmation dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Remove song?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove this song from the queue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}